import { Routes, Route } from 'react-router-dom';
import React from 'react';
import { PeopleDashboard } from './PeopleDashboard';
import { EmployeeList } from './employees/EmployeeList';
import { EmployeeDetail } from './employees/EmployeeDetail';
import { CreateEmployee } from './employees/CreateEmployee';
import { EditEmployee } from './employees/EditEmployee';
import { AttendanceCalendar } from './attendance/AttendanceCalendar';
import { LeaveManagement } from './leave/LeaveManagement';
import { LeavePolicies } from './leave/LeavePolicies';
import { MyProfile } from './employees/MyProfile';
import { PayrollView } from './payroll/PayrollView';
import { RecruitmentPipeline } from './recruitment/RecruitmentPipeline';
import { PerformanceHub } from './performance/PerformanceHub';
import { ShiftManagement } from './shifts/ShiftManagement';
import { WorkforceDashboard } from './workforce/WorkforceDashboard';
import { OnboardingDashboard } from './onboarding/OnboardingDashboard';
import { OnboardingWizard } from './onboarding/OnboardingWizard';
import { Candidates } from './candidates/Candidates';
import { InterviewScheduler } from './interviews/InterviewScheduler';
import { Offboarding } from './offboarding/Offboarding';
import { TrainingCenter } from './training/TrainingCenter';
import { OrgChart } from './orgchart/OrgChart';
import { EmployeeDocuments } from './documents/EmployeeDocuments';
import { HolidayCalendar } from './holidays/HolidayCalendar';
import { HrPolicies } from './policies/HrPolicies';
import { HrReports } from './reports/HrReports';
import { HrSettings } from './settings/HrSettings';
import { EngagementAdmin } from './engagement/EngagementAdmin';
import { RequirePermissionRoute } from '../../components/RequirePermissionRoute';

/** Wrap an element in a route-level permission guard. */
const g = (el: React.ReactNode, moduleName: string, resource: string, action = 'View') => (
  <RequirePermissionRoute moduleName={moduleName} resource={resource} action={action}>
    {el}
  </RequirePermissionRoute>
);

export function PeopleHub() {
  return (
    <div className="flex-1 flex flex-col min-h-0 w-full h-full">
      <Routes>
        {/* Hub dashboard — open to anyone who can reach the People hub */}
        <Route index element={<PeopleDashboard />} />

        {/* Employees */}
        <Route path="/employees" element={g(<EmployeeList />, 'People', 'Employees')} />
        <Route path="/employees/new" element={g(<CreateEmployee />, 'People', 'Employees', 'Create')} />
        <Route path="/add" element={g(<CreateEmployee />, 'People', 'Employees', 'Create')} />
        <Route path="/employees/:id" element={g(<EmployeeDetail />, 'People', 'Employees')} />
        <Route path="/employees/:id/edit" element={g(<EditEmployee />, 'People', 'Employees', 'Edit')} />
        <Route path="/directory" element={g(<EmployeeList />, 'People', 'Employees')} />

        {/* Attendance */}
        <Route path="/attendance" element={g(<AttendanceCalendar />, 'Attendance', 'Dashboard')} />

        {/* Core HR */}
        <Route path="/leave" element={g(<LeaveManagement />, 'Leave', 'Leave Requests')} />
        <Route path="/leave-policies" element={g(<LeavePolicies />, 'Leave', 'Leave Types', 'Edit')} />
        <Route path="/me" element={<MyProfile />} />
        <Route path="/payroll" element={g(<PayrollView />, 'Payroll', 'Payroll Records')} />
        <Route path="/shifts" element={g(<ShiftManagement />, 'Shifts', 'Shift Assignments')} />
        <Route path="/holidays" element={g(<HolidayCalendar />, 'People', 'Holidays')} />

        {/* Recruitment */}
        <Route path="/recruitment" element={g(<RecruitmentPipeline />, 'Recruitment', 'Jobs')} />
        <Route path="/candidates" element={g(<Candidates />, 'Recruitment', 'Candidates')} />
        <Route path="/interviews" element={g(<InterviewScheduler />, 'Recruitment', 'Interviews')} />
        <Route path="/onboarding" element={g(<OnboardingDashboard />, 'Onboarding', 'Onboarding Requests')} />
        <Route path="/onboarding/:id" element={g(<OnboardingWizard />, 'Onboarding', 'Onboarding Requests')} />
        <Route path="/offboarding" element={g(<Offboarding />, 'Recruitment', 'Offboarding')} />

        {/* Talent */}
        <Route path="/performance" element={g(<PerformanceHub />, 'Performance', 'Reviews')} />
        <Route path="/training" element={g(<TrainingCenter />, 'Training', 'Training Modules')} />
        <Route path="/orgchart" element={g(<OrgChart />, 'People', 'Org Chart')} />
        <Route path="/workforce" element={g(<WorkforceDashboard />, 'Workforce', 'Service Orders')} />

        {/* Administration */}
        {/* Open to anyone in the People hub — the page itself is read-only for
            non-admins (configuration is gated by RBAC on the client and the API). */}
        <Route path="/engagement" element={<EngagementAdmin />} />
        <Route path="/documents" element={g(<EmployeeDocuments />, 'People', 'Employee Documents')} />
        <Route path="/policies" element={g(<HrPolicies />, 'People', 'HR Policies')} />

        {/* Intelligence & settings */}
        <Route path="/reports" element={g(<HrReports />, 'People', 'Reports')} />
        <Route path="/settings" element={g(<HrSettings />, 'People', 'Settings')} />
      </Routes>
    </div>
  );
}
